import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const ChatEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const isEdit = Boolean(id)
  
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    streamUrl: '',
    isActive: false
  })

  useEffect(() => {
    if (isEdit) {
      fetchChatConfig()
    }
  }, [id, isEdit])

  const fetchChatConfig = async () => {
    try {
      const response = await api.get(`/chat/${id}`)
      const { chatConfig } = response.data
      
      setFormData({
        name: chatConfig.name || '',
        streamUrl: chatConfig.streamUrl || '',
        isActive: chatConfig.isActive || false
      })
    } catch (error) {
      console.error('Failed to fetch chat config:', error)
      toast.error('Failed to load chat configuration')
      navigate('/chat')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      if (isEdit) {
        await api.put(`/chat/${id}`, formData)
        toast.success('Chat configuration updated successfully')
      } else {
        const response = await api.post('/chat', formData)
        toast.success('Chat configuration created successfully')
        navigate(`/chat/${response.data.chatConfig.id}/edit`)
      }
    } catch (error) {
      console.error('Failed to save chat config:', error)
      toast.error('Failed to save chat configuration')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading chat configuration...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[#1c1c1c] text-white p-8">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/chat')}
          className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-avenir"
        >
          <span>&lt;</span>
          BACK
        </button>
      </div>

      {/* Main Content */}
      <main>
        <div className="max-w-2xl mx-auto">
          
          <form onSubmit={handleSubmit} className="space-y-6">
              <div className="form-group">
                <label htmlFor="name" className="block text-white text-sm font-medium mb-2">
                  Chat Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
                  placeholder="Enter chat configuration name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="streamUrl" className="block text-white text-sm font-medium mb-2">
                  Stream URL
                </label>
                <input
                  type="url"
                  id="streamUrl"
                  name="streamUrl"
                  value={formData.streamUrl}
                  onChange={handleChange}
                  className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
                  placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                  required
                />
                <p className="text-sm text-gray-400 mt-2">
                  Enter the full YouTube live stream URL
                </p>
              </div>

              <div className="form-group">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-4 h-4 text-red-600 bg-black border border-white rounded focus:ring-red-500 focus:ring-2"
                  />
                  <span className="text-white">Active (Enable real-time chat updates)</span>
                </label>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-8 py-3 font-medium transition-colors"
                >
                  {saving ? (
                    <div className="flex items-center gap-2">
                      <div className="spinner w-4 h-4"></div>
                      Saving...
                    </div>
                  ) : (
                    isEdit ? 'UPDATE CHAT' : 'CREATE CHAT'
                  )}
                </button>
              </div>
            </form>

          {/* Instructions */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-white mb-4">Setup Instructions</h2>
            <div className="space-y-4 text-gray-300">
              <div>
                <h3 className="font-medium text-white mb-2">1. Get YouTube Stream URL</h3>
                <p className="text-sm">
                  Copy the full URL from your YouTube live stream (e.g., https://www.youtube.com/watch?v=VIDEO_ID)
                </p>
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">2. Configure Chat Settings</h3>
                <p className="text-sm">
                  Set up your chat configuration with a descriptive name and enable real-time updates if needed.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">3. Use Control & Host Pages</h3>
                <p className="text-sm">
                  Use the Control page to moderate comments and the Host page to display them during your broadcast.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default ChatEditor
